import { useEffect, useState } from "react";
import { db, functions } from "./firebase";
import {
  collection, doc, onSnapshot, query, where, writeBatch, setDoc, updateDoc, deleteDoc, getDoc, getDocs,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { AccessMessage } from "./BookingThanks";
import { inputStyle, btnStyle, btnSecondaryStyle, cardStyle, labelStyle, colors } from "./lib/theme";
import { dateKey, todayKey, formatDate } from "./lib/weekUtils";
import {
  BLOCK_MINUTES, DEFAULT_SETTINGS, timeToMin, formatMin, formatPence, parseDateKey,
  slotId, slotIdsFor, isSlotTaken, priceFor, maxLengthFrom,
  floodlightBlocks, floodlightFee, blockApplies,
} from "./lib/booking";

function GameCard({ booking, user, isFree, isAdmin, joinFeePence, holdMinutes, onError }) {
  var [spots, setSpots] = useState([]);
  var [access, setAccess] = useState(null);

  function showAccess() {
    httpsCallable(functions, "getAccessInfo")({ bookingId: booking.id })
      .then(function (res) { setAccess(res.data); })
      .catch(function (err) { onError("Couldn't get the padlock code (" + (err.message || err.code) + "). Please try again in a moment."); });
  }

  useEffect(function () {
    return onSnapshot(collection(db, "bookings", booking.id, "spots"), function (snap) {
      setSpots(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
  }, [booking.id]);

  var now = Date.now();
  // A spot older members hold is "confirmed" (or has no status at all); an unpaid
  // non-member hold that has run out no longer reserves anything.
  function holdsSpot(s) { return !(s.status === "pending" && typeof s.holdUntil === "number" && s.holdUntil < now); }
  var live = spots.filter(holdsSpot);
  var mine = live.find(function (s) { return s.uid === user.uid; });
  var isHost = booking.uid === user.uid;
  var full = live.length >= booking.gameSpots;

  function startPayment(spotId) {
    return httpsCallable(functions, "createGameCheckoutSession")({ bookingId: booking.id, spot: spotId })
      .then(function (res) {
        if (res.data.url) window.location.href = res.data.url;
        else showAccess(); // nothing to pay after all — the server confirmed the spot
      })
      .catch(function (err) {
        onError("Couldn't start payment (" + (err.message || err.code) + "). Your spot is only held for a few minutes — try Pay now.");
      });
  }

  function join() {
    var taken = {};
    live.forEach(function (s) { taken[s.id] = true; });
    // Reuse my own lapsed hold if I have one, otherwise the first free spot.
    var stale = spots.find(function (s) { return s.uid === user.uid && !holdsSpot(s); });
    var free = stale ? stale.id : null;
    for (var n = 1; !free && n <= booking.gameSpots; n++) {
      if (!taken[String(n)]) free = String(n);
    }
    if (!free) return;
    var t = Date.now();
    setDoc(doc(db, "bookings", booking.id, "spots", free), {
      uid: user.uid, displayName: user.displayName, joinedAt: new Date().toISOString(), paid: false,
      status: isFree ? "confirmed" : "pending",
      holdUntil: isFree ? null : t + holdMinutes * 60000,
    }).then(function () {
      if (!isFree) return startPayment(free);
    }).catch(function (err) {
      onError(err && err.code === "permission-denied"
        ? "Couldn't join — the booking permissions haven't been updated yet. If you're an admin, publish the latest firestore.rules in Firebase."
        : "Couldn't join — that game may have just filled up.");
    });
  }

  function remove(spot) {
    if (spot.paid && !isAdmin) {
      onError("Paid spots can only be cancelled by an admin so the refund is handled — please get in touch.");
      return;
    }
    deleteDoc(doc(db, "bookings", booking.id, "spots", spot.id));
  }

  return (
    <div style={{ ...cardStyle, marginBottom: 12, padding: "16px 18px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>
            {formatDate(parseDateKey(booking.date)) + " · " + formatMin(booking.startMin) + " – " + formatMin(booking.endMin)}
          </div>
          <div style={{ fontSize: 12, color: colors.textDim }}>
            {"Court " + booking.court + " · hosted by " + booking.displayName}
          </div>
          {booking.gameNote && <div style={{ fontSize: 12, color: colors.textFaint, marginTop: 2 }}>{booking.gameNote}</div>}
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: full ? colors.danger : colors.accent }}>
            {(live.length + 1) + "/" + (booking.gameSpots + 1)}
          </div>
          <div style={{ fontSize: 11, color: colors.textDim }}>{(booking.gameSpots === 1 ? "singles" : "doubles") + " — players"}</div>
        </div>
      </div>

      {live.length > 0 && (
        <div style={{ fontSize: 12, color: colors.textDim, marginTop: 8 }}>
          {live.map(function (s) {
            return (
              <span key={s.id} style={{ marginRight: 10 }}>
                {s.displayName + (s.status === "pending" ? " (paying…)" : "")}
                {(isHost || isAdmin) && s.uid !== user.uid && (!s.paid || isAdmin) && (
                  <button onClick={function () { remove(s); }} style={{ background: "none", border: "none", color: colors.danger, cursor: "pointer", fontSize: 11 }}>×</button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {!isHost && (
        <div style={{ marginTop: 10 }}>
          {mine && mine.status === "pending" ? (
            <div style={{ display: "flex", gap: 8 }}>
              <button style={btnStyle} onClick={function () { startPayment(mine.id); }}>{"Pay " + formatPence(joinFeePence) + " to confirm"}</button>
              <button style={{ ...btnSecondaryStyle, width: "auto", padding: "12px 16px" }} onClick={function () { remove(mine); }}>Cancel</button>
            </div>
          ) : mine ? (
            <div>
              {access ? (
                <div style={{ background: colors.tealTint, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                  <AccessMessage info={access} opener="Thank you for joining the game." />
                </div>
              ) : (
                <button style={{ ...btnSecondaryStyle, marginBottom: 8 }} onClick={showAccess}>Show padlock code</button>
              )}
              <button style={btnSecondaryStyle} onClick={function () { remove(mine); }}>Leave this game</button>
            </div>
          ) : (
            <div>
              <button style={full ? { ...btnSecondaryStyle, opacity: 0.5, cursor: "not-allowed" } : btnStyle} disabled={full} onClick={join}>
                {full ? "Game full" : isFree ? "Join this game" : "Join — pay " + formatPence(joinFeePence)}
              </button>
              {!isFree && !full && (
                <div style={{ fontSize: 11, color: colors.textFaint, marginTop: 6 }}>
                  {"Non-members pay to join. Your spot is held for " + holdMinutes + " minutes while you pay."}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// `user` is null when logged out: the page then shows availability and prices
// read-only, with a prompt to log in to actually book.
export default function CourtsPage({ user, onLogIn, newJoiners, onJoinersSeen }) {
  var uid = user ? user.uid : null;
  var readOnly = !user;
  var [settingsDoc, setSettingsDoc] = useState(undefined); // undefined = loading, null = not set up
  var [me, setMe] = useState({ isMember: false, isAdmin: !!(user && user.isAdmin) });
  var [date, setDate] = useState(todayKey());
  var [slots, setSlots] = useState([]);
  var [bookings, setBookings] = useState([]);
  var [form, setForm] = useState({ court: 1, startMin: null, lengthMin: null, openGame: false, gameSpots: 3, gameNote: "" });
  var [gameEdit, setGameEdit] = useState({}); // bookingId -> { spots, note }
  var [conflicts, setConflicts] = useState([]);
  var [courtBlocks, setCourtBlocks] = useState([]);
  var [access, setAccess] = useState({}); // bookingId -> padlock details from the server
  var [busy, setBusy] = useState(false);
  var [message, setMessage] = useState(null); // { type: "error" | "ok", text }

  // Pop-up messages tidy themselves away (errors stay longer so they can be read).
  useEffect(function () {
    if (!message) return;
    var t = setTimeout(function () { setMessage(null); }, message.type === "error" ? 12000 : 6000);
    return function () { clearTimeout(t); };
  }, [message]);

  var settings = { ...DEFAULT_SETTINGS, ...(settingsDoc || {}) };
  var isFree = me.isMember || me.isAdmin;

  useEffect(function () {
    var unsubSettings = onSnapshot(doc(db, "settings", "booking"), function (snap) {
      setSettingsDoc(snap.exists() ? snap.data() : null);
    }, function () {
      // Not readable (e.g. permissions not published yet) — show "not open" rather than loading forever.
      setSettingsDoc(null);
    });
    var unsubBlocks = onSnapshot(collection(db, "courtBlocks"), function (snap) {
      setCourtBlocks(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    // Who booked what is private, so only fetched when logged in.
    var unsubMe = function () {};
    var unsubBookings = function () {};
    if (uid) {
      unsubMe = onSnapshot(doc(db, "users", uid), function (snap) {
        var d = snap.exists() ? snap.data() : {};
        setMe({ isMember: !!d.isMember, isAdmin: !!d.isAdmin });
      });
      unsubBookings = onSnapshot(query(collection(db, "bookings"), where("date", ">=", todayKey())), function (snap) {
        setBookings(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
      });
    }
    return function () { unsubSettings(); unsubMe(); unsubBookings(); unsubBlocks(); };
  }, [uid]);

  useEffect(function () {
    if (!settingsDoc || !settingsDoc.venueId) { setSlots([]); return; }
    var q = query(collection(db, "courtSlots"), where("venueId", "==", settingsDoc.venueId), where("date", "==", date));
    return onSnapshot(q, function (snap) {
      setSlots(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
  }, [settingsDoc, date]);

  // Payments that went through but couldn't be honoured — admins refund these.
  useEffect(function () {
    if (!me.isAdmin) { setConflicts([]); return; }
    return onSnapshot(collection(db, "paymentConflicts"), function (snap) {
      setConflicts(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
  }, [me.isAdmin]);

  // Coming back from Stripe Checkout.
  useEffect(function () {
    var params = new URLSearchParams(window.location.search);
    var result = params.get("booking");
    if (result === "success") setMessage({ type: "ok", text: "Payment received — your booking will show as confirmed in a moment." });
    if (result === "joined") setMessage({ type: "ok", text: "Payment received — you'll show as joined in the game in a moment." });
    if (result === "cancelled") setMessage({ type: "error", text: "Payment was cancelled. Your court is only held for a few minutes — pay from My Bookings to keep it." });
    if (result) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  if (settingsDoc === undefined) return <p style={{ fontSize: 13, color: colors.textDim }}>Loading…</p>;

  if (settingsDoc === null || !settingsDoc.venueId) {
    return (
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Court Booking</h3>
        <p style={{ margin: 0, fontSize: 14, color: colors.textDim }}>
          {me.isAdmin
            ? "Booking isn't set up yet — choose the venue, hours and prices in Admin → Court Booking."
            : "Court booking isn't open yet — check back soon."}
        </p>
      </div>
    );
  }

  var now = Date.now();
  var openMin = timeToMin(settings.openTime);
  var closeMin = timeToMin(settings.closeTime);
  var courtCount = settings.courtCount || 1;
  var minMinutes = isFree ? settings.minMinutesMember : settings.minMinutesNonMember;

  var slotsById = {};
  slots.forEach(function (s) { slotsById[s.id] = s; });
  var bookingsById = {};
  bookings.forEach(function (b) { bookingsById[b.id] = b; });

  var nowDate = new Date();
  var nowMin = nowDate.getHours() * 60 + nowDate.getMinutes();
  var isToday = date === todayKey();

  function isPast(m) { return isToday && m + BLOCK_MINUTES <= nowMin; }

  var blocks = [];
  for (var m = openMin; m < closeMin; m += BLOCK_MINUTES) blocks.push(m);

  var dateOptions = [];
  for (var i = 0; i <= settings.advanceDays; i++) {
    var d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + i);
    dateOptions.push({ key: dateKey(d), label: formatDate(d) });
  }

  // A recurring club session reserved by an admin at this court and time, if any.
  function blockAt(court, minute) {
    return courtBlocks.find(function (b) {
      return b.venueId === settings.venueId && blockApplies(b, date, court, minute);
    });
  }

  function lenFrom(court, startMin) {
    return maxLengthFrom(slotsById, settings.venueId, date, court, startMin, closeMin, settings.maxMinutes, now,
      function (mm) { return !!blockAt(court, mm); });
  }

  var courtOptions = [];
  for (var c = 1; c <= courtCount; c++) courtOptions.push(c);

  var startOptions = blocks.filter(function (bm) {
    return !isPast(bm) && lenFrom(form.court, bm) >= minMinutes;
  });
  var startMin = startOptions.indexOf(form.startMin) !== -1 ? form.startMin : null;
  var maxLen = startMin !== null ? lenFrom(form.court, startMin) : 0;
  var lengthMin = startMin !== null ? Math.min(Math.max(form.lengthMin || minMinutes, minMinutes), maxLen) : null;
  var lengthOptions = [];
  for (var l = minMinutes; l <= maxLen; l += BLOCK_MINUTES) lengthOptions.push(l);
  // Court hire is free for members; floodlights are charged to everyone except
  // admins. A booking only confirms instantly when there's nothing to pay.
  var courtFee = startMin !== null && !isFree ? priceFor(settings.pricePence, startMin, startMin + lengthMin) : 0;
  var litBlocks = startMin !== null ? floodlightBlocks(startMin, startMin + lengthMin, settings.floodlightsFromMin) : 0;
  var lightsFee = startMin !== null && !me.isAdmin ? floodlightFee(settings.floodlightRatePence, litBlocks) : 0;
  var price = courtFee + lightsFee;
  var autoConfirm = me.isAdmin || (me.isMember && lightsFee === 0);
  var lightsOn = settings.floodlightsFromMin < 1440;

  function patch(p) { setForm(function (prev) { return { ...prev, ...p }; }); }

  function startPayment(bookingId) {
    return httpsCallable(functions, "createCheckoutSession")({ bookingId: bookingId })
      .then(function (res) {
        if (res.data.url) { window.location.href = res.data.url; return; }
        // Nothing needed paying after all, so the server just confirmed it.
        setMessage({ type: "ok", text: "Booked!" });
        setBusy(false);
        showAccess(bookingId);
      })
      .catch(function (err) {
        setMessage({ type: "error", text: "Couldn't start payment (" + (err.message || err.code) + "). Your court is held for a few minutes — try Pay now under My Bookings." });
        setBusy(false);
      });
  }

  // Asks the server for the padlock code; it only answers if this is your confirmed booking.
  function showAccess(bookingId) {
    return httpsCallable(functions, "getAccessInfo")({ bookingId: bookingId })
      .then(function (res) { setAccess(function (prev) { return { ...prev, [bookingId]: res.data }; }); })
      .catch(function (err) {
        setMessage({ type: "error", text: "Couldn't get the padlock code (" + (err.message || err.code) + "). Please try again in a moment." });
      });
  }

  function book() {
    if (startMin === null) return;
    var endMin = startMin + lengthMin;
    var ids = slotIdsFor(settings.venueId, date, form.court, startMin, endMin);
    var t = Date.now();
    if (ids.some(function (id) { return isSlotTaken(slotsById[id], t); })) {
      setMessage({ type: "error", text: "Someone just took part of that time — please pick another." });
      return;
    }
    for (var bm = startMin; bm < endMin; bm += BLOCK_MINUTES) {
      if (blockAt(form.court, bm)) {
        setMessage({ type: "error", text: "That time is reserved for a club session — please pick another." });
        return;
      }
    }
    setBusy(true);
    setMessage(null);
    var holdUntil = t + settings.holdMinutes * 60000;
    var ref = doc(collection(db, "bookings"));
    var batch = writeBatch(db);
    batch.set(ref, {
      venueId: settings.venueId, venueName: settings.venueName || "", date: date, court: form.court,
      startMin: startMin, endMin: endMin, slotIds: ids,
      uid: user.uid, displayName: user.displayName, isMember: !!me.isMember,
      status: autoConfirm ? "confirmed" : "pending", paid: false,
      amountPence: autoConfirm ? 0 : price,
      holdUntil: autoConfirm ? null : holdUntil,
      gameOpen: !!form.openGame, gameSpots: form.openGame ? form.gameSpots : 0, gameNote: form.openGame ? form.gameNote.trim() : "",
      createdAt: new Date().toISOString(),
    });
    ids.forEach(function (id, idx) {
      batch.set(doc(db, "courtSlots", id), {
        venueId: settings.venueId, date: date, court: form.court, startMin: startMin + idx * BLOCK_MINUTES,
        bookingId: ref.id, uid: user.uid, status: autoConfirm ? "confirmed" : "pending",
        holdUntil: autoConfirm ? null : holdUntil, paid: false,
      });
    });
    batch.commit().then(function () {
      patch({ startMin: null, lengthMin: null, openGame: false, gameNote: "" });
      if (autoConfirm) {
        setMessage({ type: "ok", text: "Booked! " + formatMin(startMin) + " – " + formatMin(endMin) + ", court " + form.court + "." });
        setBusy(false);
        showAccess(ref.id);
      } else {
        return startPayment(ref.id);
      }
    }).catch(function (err) {
      setMessage({
        type: "error",
        text: err && err.code === "permission-denied"
          ? "Couldn't book — the booking permissions haven't been updated yet. If you're an admin, publish the latest firestore.rules in Firebase."
          : "That time couldn't be booked — it may have just been taken. Please pick another.",
      });
      setBusy(false);
    });
  }

  function cancelBooking(b) {
    var paid = b.paid;
    if (paid && !me.isAdmin) {
      setMessage({ type: "error", text: "Paid bookings can only be cancelled by an admin so the refund is handled — please get in touch." });
      return;
    }
    if (!window.confirm("Cancel this booking?" + (paid ? " It was paid for, so remember to refund it in Stripe." : ""))) return;
    Promise.all([
      getDocs(collection(db, "bookings", b.id, "spots")),
      Promise.all((b.slotIds || []).map(function (id) { return getDoc(doc(db, "courtSlots", id)); })),
    ]).then(function (res) {
      var paidJoiners = res[0].docs.filter(function (s) { return s.data().paid; });
      if (paidJoiners.length > 0 && !me.isAdmin) {
        setMessage({ type: "error", text: "Players have paid to join this game, so only an admin can cancel it (they'll need refunding) — please get in touch." });
        return;
      }
      if (paidJoiners.length > 0 && !window.confirm(paidJoiners.length + " player(s) paid to join this game — remember to refund them in Stripe. Cancel anyway?")) return;
      var batch = writeBatch(db);
      res[0].docs.forEach(function (s) { batch.delete(s.ref); });
      // Only remove blocks that are still this booking's (an expired hold may have been taken over).
      res[1].forEach(function (s) { if (s.exists() && s.data().bookingId === b.id) batch.delete(s.ref); });
      batch.delete(doc(db, "bookings", b.id));
      return batch.commit();
    }).catch(function () {
      setMessage({ type: "error", text: "Couldn't cancel that booking." });
    });
  }

  function setGame(b, open) {
    var e = gameEdit[b.id] || {};
    updateDoc(doc(db, "bookings", b.id), {
      gameOpen: open,
      gameSpots: open ? (e.spots || 3) : 0,
      gameNote: open ? (e.note !== undefined ? e.note : (b.gameNote || "")).trim() : "",
    });
  }

  var myBookings = bookings.filter(function (b) { return b.uid === uid; })
    .sort(function (a, b) { return (a.date + a.startMin).localeCompare(b.date + b.startMin) || a.startMin - b.startMin; });
  var openGames = bookings.filter(function (b) { return b.gameOpen && b.status === "confirmed"; })
    .sort(function (a, b) { return a.date.localeCompare(b.date) || a.startMin - b.startMin; });
  var allBookings = bookings.slice()
    .sort(function (a, b) { return a.date.localeCompare(b.date) || a.startMin - b.startMin; });

  function statusLabel(b) {
    if (b.status === "payment_conflict") return "PAID BUT COURT WAS TAKEN — refund in Stripe";
    if (b.status === "confirmed") return b.paid ? "Paid" : "Confirmed";
    if (typeof b.holdUntil === "number" && b.holdUntil < now) return "Hold expired";
    return "Awaiting payment";
  }

  return (
    <div>
      <h2 style={{ fontSize: 18, marginBottom: 4 }}>{"Courts — " + (settings.venueName || "Booking")}</h2>
      <p style={{ fontSize: 13, color: colors.textDim, marginTop: 0, marginBottom: 14 }}>
        {(isFree
          ? "Members book free — 30-minute blocks, up to " + (settings.maxMinutes / 60) + " hours."
          : "Courts are " + formatPence(settings.pricePence) + " per 30 minutes, with a minimum booking of " + (settings.minMinutesNonMember / 60) + " hour."
            + (readOnly ? " Members book free." : ""))
          + (lightsOn && !me.isAdmin ? " Floodlights are " + formatPence(settings.floodlightRatePence) + " per hour from " + formatMin(settings.floodlightsFromMin) + "." : "")}
      </p>

      {message && (
        // Pinned to the bottom of the screen so it's seen wherever you've scrolled to (e.g. after Cancel under My Bookings).
        <div onClick={function () { setMessage(null); }} style={{
          position: "fixed", left: 16, right: 16, bottom: 20, maxWidth: 480, margin: "0 auto", zIndex: 50,
          background: message.type === "error" ? colors.dangerBg : colors.successBg,
          border: "1px solid " + (message.type === "error" ? colors.dangerBorder : colors.successBorder),
          color: message.type === "error" ? colors.danger : colors.success,
          boxShadow: "0 4px 16px rgba(0,0,0,0.18)", cursor: "pointer",
          borderRadius: 8, padding: "12px 16px", fontSize: 13,
        }}>
          {message.text}
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>Tap to dismiss</div>
        </div>
      )}

      {newJoiners && newJoiners.length > 0 && (
        <div style={{ background: colors.successBg, border: "1px solid " + colors.successBorder, borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.success, marginBottom: 4 }}>
            {newJoiners.length === 1 ? "A new player joined your game" : newJoiners.length + " new players joined your games"}
          </div>
          {newJoiners.map(function (j) {
            return (
              <div key={j.key} style={{ fontSize: 13, color: colors.text }}>
                {j.name + " — " + formatDate(parseDateKey(j.date)) + ", " + formatMin(j.startMin) + ", court " + j.court}
              </div>
            );
          })}
          <button onClick={onJoinersSeen} style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 14px", fontSize: 12, marginTop: 8 }}>Got it</button>
        </div>
      )}

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={labelStyle}>Day</div>
        <select style={{ ...inputStyle, marginBottom: 14 }} value={date} onChange={function (e) { setDate(e.target.value); patch({ startMin: null }); }}>
          {dateOptions.map(function (o) { return <option key={o.key} value={o.key}>{o.label}</option>; })}
        </select>

        <div style={{ overflowX: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "56px repeat(" + courtCount + ", minmax(52px, 1fr))", gap: 2, fontSize: 11 }}>
            <div />
            {courtOptions.map(function (c) { return <div key={c} style={{ textAlign: "center", color: colors.textDim, fontWeight: 700 }}>{"Court " + c}</div>; })}
            {blocks.map(function (bm) {
              return [
                <div key={"t" + bm} style={{ color: colors.textDim, paddingTop: 5 }}>{formatMin(bm)}</div>,
                courtOptions.map(function (c) {
                  var slot = slotsById[slotId(settings.venueId, date, c, bm)];
                  var club = blockAt(c, bm);
                  var taken = isSlotTaken(slot, now) || !!club;
                  var past = isPast(bm);
                  var bk = slot ? bookingsById[slot.bookingId] : null;
                  var selected = startMin !== null && form.court === c && bm >= startMin && bm < startMin + lengthMin;
                  var lit = bm >= settings.floodlightsFromMin;
                  var bg = selected ? colors.teal : club ? "rgba(124,148,32,0.35)" : taken ? colors.tealTint : past ? "#EEF2F2" : lit ? "#FFF3C4" : colors.limeTint;
                  return (
                    <button
                      key={"c" + c + "_" + bm}
                      disabled={taken || past || readOnly}
                      onClick={function () { patch({ court: c, startMin: bm, lengthMin: null }); }}
                      style={{
                        background: bg, border: "none", borderRadius: 4, height: 26, padding: 0, fontSize: 10,
                        color: selected ? "#fff" : colors.textDim, cursor: taken || past || readOnly ? "default" : "pointer", overflow: "hidden",
                      }}
                    >
                      {club ? club.label : taken ? (bk ? bk.displayName.split(" ")[0] + (bk.gameOpen ? " ★" : "") : (slot && slot.status === "confirmed" ? "Booked" : "held")) : ""}
                    </button>
                  );
                }),
              ];
            })}
          </div>
        </div>
        <div style={{ fontSize: 11, color: colors.textFaint, marginTop: 8 }}>
          {"Tap a free (green) block to start a booking. ★ = open game you can join. Dark green = club session."}
          {lightsOn && !me.isAdmin ? " Yellow = floodlit (" + formatPence(settings.floodlightRatePence) + "/hour extra, from " + formatMin(settings.floodlightsFromMin) + ")." : ""}
        </div>
      </div>

      {readOnly ? (
        <div style={{ ...cardStyle, textAlign: "center" }}>
          <h3 style={{ marginTop: 0, fontSize: 15 }}>Want to book a court?</h3>
          <p style={{ fontSize: 13, color: colors.textDim, marginTop: 0 }}>
            Log in or create a free account to book. Members play free; everyone else pays by card at the time of booking.
          </p>
          {onLogIn && <button style={{ ...btnStyle, width: "auto", padding: "12px 28px" }} onClick={onLogIn}>Log in / Sign up</button>}
        </div>
      ) : (
      <>
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Book a Court</h3>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Court</div>
            <select style={inputStyle} value={form.court} onChange={function (e) { patch({ court: parseInt(e.target.value, 10), startMin: null }); }}>
              {courtOptions.map(function (c) { return <option key={c} value={c}>{"Court " + c}</option>; })}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Start</div>
            <select style={inputStyle} value={startMin === null ? "" : startMin} onChange={function (e) { patch({ startMin: e.target.value === "" ? null : parseInt(e.target.value, 10), lengthMin: null }); }}>
              <option value="">Choose…</option>
              {startOptions.map(function (bm) { return <option key={bm} value={bm}>{formatMin(bm)}</option>; })}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Length</div>
            <select style={inputStyle} disabled={startMin === null} value={lengthMin === null ? "" : lengthMin} onChange={function (e) { patch({ lengthMin: parseInt(e.target.value, 10) }); }}>
              {lengthOptions.map(function (len) { return <option key={len} value={len}>{len % 60 === 0 ? (len / 60) + "h" : len + " min"}</option>; })}
            </select>
          </div>
        </div>
        {startOptions.length === 0 && (
          <p style={{ fontSize: 12, color: colors.textDim, marginTop: 0 }}>
            {"No free " + (minMinutes / 60 >= 1 ? "hour-long " : "") + "slots left on this court today — try another court or day."}
          </p>
        )}

        <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <input type="checkbox" checked={form.openGame} onChange={function (e) { patch({ openGame: e.target.checked }); }} />
          Open this up as a game other players can join
        </label>
        {form.openGame && (
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <div style={{ width: 150 }}>
              <div style={labelStyle}>Game type</div>
              <select style={inputStyle} value={form.gameSpots === 1 ? 1 : 3} onChange={function (e) { patch({ gameSpots: parseInt(e.target.value, 10) }); }}>
                <option value={1}>Singles (2 players)</option>
                <option value={3}>Doubles (4 players)</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Note (level, format…)</div>
              <input style={inputStyle} value={form.gameNote} onChange={function (e) { patch({ gameNote: e.target.value }); }} placeholder="e.g. Intermediate, doubles" />
            </div>
          </div>
        )}

        <button style={btnStyle} disabled={startMin === null || busy} onClick={book}>
          {busy ? "Working…" : startMin === null ? "Choose a time"
            : autoConfirm ? (me.isAdmin ? "Book" : "Book — free for members") : "Book and pay " + formatPence(price)}
        </button>
        {startMin !== null && lightsFee > 0 && (
          <p style={{ fontSize: 11, color: colors.textDim, marginBottom: 0 }}>
            {(courtFee > 0 ? "Court " + formatPence(courtFee) + " + " : "") + "floodlights " + formatPence(lightsFee) + " (" + (litBlocks / 2) + "h lit)."}
          </p>
        )}
        {!autoConfirm && startMin !== null && (
          <p style={{ fontSize: 11, color: colors.textFaint, marginBottom: 0 }}>
            {"You'll be taken to Stripe to pay. The court is held for " + settings.holdMinutes + " minutes while you do."}
          </p>
        )}
      </div>

      <h3 style={{ fontSize: 15, marginBottom: 8 }}>Open Games</h3>
      {openGames.length === 0 && <p style={{ fontSize: 13, color: colors.textDim, marginTop: 0 }}>No open games right now — book a court and open one up!</p>}
      {openGames.map(function (b) {
        return (
          <GameCard
            key={b.id} booking={b} user={user}
            isFree={isFree} isAdmin={me.isAdmin}
            joinFeePence={settings.gameJoinFeePence} holdMinutes={settings.holdMinutes}
            onError={function (t) { setMessage({ type: "error", text: t }); }}
          />
        );
      })}

      <h3 style={{ fontSize: 15, marginBottom: 8, marginTop: 20 }}>My Bookings</h3>
      {myBookings.length === 0 && <p style={{ fontSize: 13, color: colors.textDim, marginTop: 0 }}>You have no upcoming bookings.</p>}
      {myBookings.map(function (b) {
        var expired = b.status === "pending" && typeof b.holdUntil === "number" && b.holdUntil < now;
        var e = gameEdit[b.id] || {};
        return (
          <div key={b.id} style={{ ...cardStyle, marginBottom: 12, padding: "16px 18px", opacity: expired ? 0.6 : 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>
                  {formatDate(parseDateKey(b.date)) + " · " + formatMin(b.startMin) + " – " + formatMin(b.endMin)}
                </div>
                <div style={{ fontSize: 12, color: colors.textDim }}>
                  {"Court " + b.court + " · " + statusLabel(b) + (b.amountPence ? " · " + formatPence(b.amountPence) : "")}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {b.status === "pending" && !expired && (
                  <button style={{ ...btnStyle, width: "auto", padding: "6px 12px", fontSize: 12 }} onClick={function () { setBusy(true); startPayment(b.id); }}>Pay now</button>
                )}
                <button style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 12px", fontSize: 12, color: colors.danger, borderColor: colors.dangerBorder }} onClick={function () { cancelBooking(b); }}>
                  {expired ? "Remove" : "Cancel"}
                </button>
              </div>
            </div>
            {b.status === "confirmed" && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid " + colors.border }}>
                {access[b.id] ? (
                  <div style={{ background: colors.tealTint, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                    <AccessMessage info={access[b.id]} opener="Thank you for booking a court." />
                  </div>
                ) : (
                  <button style={{ ...btnSecondaryStyle, marginBottom: 10, fontSize: 13 }} onClick={function () { showAccess(b.id); }}>Show padlock code</button>
                )}
                {b.gameOpen ? (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: colors.textDim }}>
                    <span>{"Open game — " + (b.gameSpots === 1 ? "singles" : "doubles") + ", " + (b.gameSpots + 1) + " players including you"}</span>
                    <button style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 12px", fontSize: 12 }} onClick={function () { setGame(b, false); }}>Close game</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select style={{ ...inputStyle, width: 150 }} value={e.spots === 1 ? 1 : 3} onChange={function (ev) { var v = parseInt(ev.target.value, 10); setGameEdit(function (p) { return { ...p, [b.id]: { ...(p[b.id] || {}), spots: v } }; }); }}>
                      <option value={1}>Singles (2)</option>
                      <option value={3}>Doubles (4)</option>
                    </select>
                    <input style={inputStyle} placeholder="Note (optional)" value={e.note || ""} onChange={function (ev) { var v = ev.target.value; setGameEdit(function (p) { return { ...p, [b.id]: { ...(p[b.id] || {}), note: v } }; }); }} />
                    <button style={{ ...btnSecondaryStyle, width: "auto", padding: "8px 12px", fontSize: 12, whiteSpace: "nowrap" }} onClick={function () { setGame(b, true); }}>Open as game</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {me.isAdmin && (
        <div style={{ ...cardStyle, marginTop: 20 }}>
          {conflicts.length > 0 && (
            <div style={{ background: colors.dangerBg, border: "1px solid " + colors.dangerBorder, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: colors.danger, marginBottom: 6 }}>Paid but couldn't be honoured — refund in Stripe</div>
              {conflicts.map(function (c) {
                return (
                  <div key={c.id} style={{ fontSize: 12, color: colors.text, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0" }}>
                    <span>{(c.displayName || c.uid) + " — " + (c.reason || "") + (c.amountPence ? " (" + formatPence(c.amountPence) + ")" : "")}</span>
                    <button onClick={function () { deleteDoc(doc(db, "paymentConflicts", c.id)); }} style={{ background: "none", border: "none", color: colors.textDim, cursor: "pointer", fontSize: 11, textDecoration: "underline" }}>refunded</button>
                  </div>
                );
              })}
            </div>
          )}
          <h3 style={{ marginTop: 0, fontSize: 15 }}>All Upcoming Bookings (admin)</h3>
          {allBookings.length === 0 && <p style={{ fontSize: 13, color: colors.textDim }}>None.</p>}
          {allBookings.map(function (b) {
            return (
              <div key={b.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid " + colors.border, fontSize: 13 }}>
                <div>
                  <strong>{b.displayName}</strong>
                  <div style={{ fontSize: 12, color: colors.textDim }}>
                    {formatDate(parseDateKey(b.date)) + " · " + formatMin(b.startMin) + " – " + formatMin(b.endMin) + " · Court " + b.court + " · " + statusLabel(b) + (b.amountPence ? " · " + formatPence(b.amountPence) : "")}
                  </div>
                </div>
                <button style={{ ...btnSecondaryStyle, width: "auto", padding: "6px 10px", fontSize: 12, color: colors.danger, borderColor: colors.dangerBorder }} onClick={function () { cancelBooking(b); }}>Cancel</button>
              </div>
            );
          })}
        </div>
      )}
      </>
      )}
    </div>
  );
}
